import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Truck, Globe, MapPin, Phone, Mail, Shield, Clock, CheckCircle2, DollarSign, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const ShippingCanadaToNepalPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', weight: '' });
  const { toast } = useToast();
  
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ 
      title: "Quote Request Received!", 
      description: "We'll contact you within 24 hours with shipping rates to Nepal." 
    });
    setFormData({ name: '', email: '', phone: '', weight: '' });
  };

  const pricingData = [
    { weight: 'Documents (Standard)', price: 'CAD $44', delivery: '7-9 business days' },
    { weight: 'Documents (Express)', price: 'CAD $77', delivery: '2-5 business days' },
    { weight: '1 kg Parcel', price: 'CAD $45', delivery: '5-7 business days' },
    { weight: '5 kg Parcel', price: 'CAD $85', delivery: '5-7 business days' },
    { weight: '10 kg Parcel', price: 'CAD $149', delivery: '5-7 business days' },
    { weight: '15 kg Parcel', price: 'CAD $195', delivery: '5-7 business days' },
    { weight: '23 kg Slab', price: 'CAD $9.99/kg', delivery: '5-10 business days', highlight: true },
    { weight: '30 kg Slab', price: 'CAD $9.99/kg', delivery: '5-10 business days', highlight: true }
  ];

  const nepalCities = [
    'Kathmandu', 'Pokhara', 'Lalitpur', 'Biratnagar', 'Bharatpur', 'Butwal'
  ];

  const services = [
    'Parcels and packages to Nepal',
    'Document courier to Nepal',
    'Personal belongings shipping',
    'Student luggage shipping',
    'Business shipments',
    'Door-to-door delivery',
    'E-commerce shipments',
    'Gift parcels'
  ];

  const faqs = [
    {
      question: 'How long does shipping from Canada to Nepal take?',
      answer: 'Standard parcel shipping typically takes 5-10 business days. Express document services can deliver in 2-5 business days. Delivery times depend on customs clearance and the specific destination in Nepal.'
    },
    {
      question: 'What are the shipping costs to Nepal?',
      answer: 'Rates start at CAD $44 for standard documents and CAD $45 for 1kg parcels. For shipments of 23kg or more, we offer competitive rates at CAD $9.99 per kg. Contact us for an exact quote based on your shipment details.'
    },
    {
      question: 'What items can I ship to Nepal?',
      answer: 'You can ship documents, parcels, personal belongings, gifts, business goods, and student luggage. Prohibited items include weapons, illegal substances, and perishable foods. Always declare items accurately for customs clearance.'
    },
    {
      question: 'Do you offer door-to-door delivery in Nepal?',
      answer: 'Yes! We provide door-to-door delivery to Kathmandu, Pokhara, Lalitpur, and other major cities in Nepal. Our partners handle local delivery once your shipment clears customs.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Shipping from Canada to Nepal | International Courier – Shippzy</title>
        <meta name="description" content="Affordable shipping from Canada to Nepal. Documents, parcels, luggage to Kathmandu, Pokhara, Lalitpur. Door-to-door courier with customs support. Rates from CAD $44." />
        <link rel="canonical" href="https://www.shippzy.ca/shipping-canada-to-nepal" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 text-white py-24 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 2px, transparent 2px, transparent 10px)',
            }}></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-block mb-4 px-4 py-2 bg-green-600/30 border border-green-400/30 rounded-full">
                  <span className="text-green-300 text-sm font-semibold">🇳🇵 Canada to Nepal Shipping</span>
                </div>
                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  Shipping from Canada
                  <span className="block bg-gradient-to-r from-green-300 to-teal-300 bg-clip-text text-transparent">to Nepal</span>
                </h1>
                <p className="text-xl text-gray-200 mb-8">
                  Reliable international courier service to Kathmandu, Pokhara, Lalitpur, and all Nepal destinations. Affordable rates starting at CAD $44.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white px-8 py-6 text-lg shadow-xl"
                    onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Get Free Quote
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                    onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                  >
                    View Pricing
                  </Button>
                </div>
                <div className="mt-8 flex items-center gap-6 text-sm text-gray-300">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>+1 (437) 616-0758</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span>ship@shippzy.ca</span>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="hidden md:block"
              >
                <div className="relative">
                  <motion.div
                    animate={{ y: [0, -20, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="bg-gradient-to-br from-green-600 to-teal-600 rounded-3xl p-8 shadow-2xl"
                  >
                    <Package className="w-32 h-32 text-white mx-auto mb-4" />
                    <div className="text-center">
                      <div className="text-4xl font-bold text-white mb-2">5-10 Days</div>
                      <div className="text-green-200">Delivery to Nepal</div>
                    </div>
                  </motion.div>
                  <div className="absolute -bottom-4 -right-4 bg-yellow-500 text-white px-6 py-3 rounded-full font-bold shadow-xl">
                    From $44
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Shipping Rates from Canada to Nepal
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Transparent pricing with no hidden fees. Applicable taxes and duties charged separately.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {pricingData.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className={`h-full hover:shadow-xl transition-all duration-300 ${
                    item.highlight ? 'border-4 border-green-600 relative' : 'border-t-4 border-green-600'
                  }`}>
                    {item.highlight && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-1 rounded-full text-sm font-bold shadow-lg">
                        BEST VALUE
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle className="text-2xl flex items-center gap-2">
                        <DollarSign className="w-6 h-6 text-green-600" />
                        {item.weight}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-green-700 mb-2">{item.price}</div>
                      <div className="text-sm text-gray-600 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {item.delivery}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 bg-green-50 border-l-4 border-green-600 p-6 rounded-lg max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <FileText className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-3">Heavy Parcel Pricing</h3>
                  <div className="space-y-2 text-gray-700 mb-3">
                    <p><strong>23 kg:</strong> CAD $229.77 (exact)</p>
                    <p><strong>30 kg:</strong> CAD $299.70 (exact)</p>
                    <p><strong>Additional Weight:</strong> CAD $15/kg</p>
                  </div>
                  <p className="text-sm text-gray-600">
                    Additional weight is charged at CAD $15/kg beyond the applicable weight slab. The 30 kg slab has its own base rate.
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    Base shipping prices. Applicable taxes and separately chargeable services extra. Customs duties not included.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services & Cities */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Services */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="h-full shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-2xl flex items-center gap-3">
                      <Package className="w-8 h-8 text-green-600" />
                      What We Ship to Nepal
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {services.map((service, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Nepal Cities */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="h-full shadow-lg border-l-4 border-green-500">
                  <CardHeader>
                    <CardTitle className="text-2xl flex items-center gap-3">
                      <MapPin className="w-8 h-8 text-green-600" />
                      Nepal Cities We Serve
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      {nepalCities.map((city, index) => (
                        <div key={index} className="flex items-center gap-2 text-gray-700">
                          <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                          <span>{city}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 mt-6">
                      We deliver to all regions across Nepal including major cities and rural areas.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
            </motion.div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="shadow-lg hover:shadow-xl transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg text-gray-900 flex items-start gap-3">
                        <FileText className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                        <span>{faq.question}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Quote Form */}
        <section id="quote-form" className="py-20 bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className="shadow-2xl">
                <CardHeader>
                  <CardTitle className="text-3xl text-center">Get Free Shipping Quote to Nepal</CardTitle>
                  <p className="text-center text-gray-600 mt-2">We'll respond within 24 hours with competitive rates</p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name *</label>
                      <Input 
                        name="name" 
                        value={formData.name} 
                        onChange={handleChange} 
                        required 
                        placeholder="John Doe" 
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                      <Input 
                        type="email" 
                        name="email" 
                        value={formData.email} 
                        onChange={handleChange} 
                        required 
                        placeholder="john@example.com" 
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                      <Input 
                        type="tel" 
                        name="phone" 
                        value={formData.phone} 
                        onChange={handleChange} 
                        required 
                        placeholder="+1 (123) 456-7890" 
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Approximate Weight *</label>
                      <Input 
                        name="weight" 
                        value={formData.weight} 
                        onChange={handleChange} 
                        required 
                        placeholder="e.g., 5 kg" 
                        className="h-12"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white h-14 text-lg font-semibold shadow-xl"
                    >
                      Get Quote Now
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Need Help with Your Nepal Shipment?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Our team is ready to assist with customs, documentation, and shipping guidance
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white px-8 py-6 text-lg"
                onClick={() => window.location.href = '/contact'}
              >
                Contact Shippzy
              </Button>
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => window.location.href = '/track-shipment'}
              >
                Track Shipment
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ShippingCanadaToNepalPage;